import { Injectable } from '@angular/core';
import { LoggerService } from 'i2g-ng-utils';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { serialize } from '../../utils/helpers/model.helper';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
} from '../auth-data/auth-http.service';
import { IResponseInfo } from '../http/http.data';
import {
  IBankTransferPayload,
  ICardPaymentsPayload,
  IPaypalPayload,
  PaymentErrorsEnum,
} from '../payments-data/payments-data.data';
import {
  I2gMoneyWebview,
  I2gMoneyContext,
  I2gMoneyStatement,
  I2gMoneyAccountSummary,
} from './i2g-money.data';
import { SubmitPlaidLinkDetailsPayload } from './settings-data.actions';
import { Settings } from './settings.model';
import { PaymentsSettings } from './sub-models/company-settings/payments.model';

export const SETTINGS_ACCEPT_VERSION = '3.19.0';
export const PAYMENTS_SETTINGS_ACCEPT_VERSION = '6.0.0';
export const I2G_MONEY_ACCEPT_VERSION = '1.0.0';

@Injectable()
export class SettingsDataService {
  private _settingsHeaders: any = {
    [ACCEPT_VERSION_HEADER]: SETTINGS_ACCEPT_VERSION,
  };
  private _paymentsSettingsHeaders: any = {
    [ACCEPT_VERSION_HEADER]: PAYMENTS_SETTINGS_ACCEPT_VERSION,
  };
  private _i2gMoneyHeaders: any = {
    [ACCEPT_VERSION_HEADER]: I2G_MONEY_ACCEPT_VERSION,
  };

  settingsUrl = '/settings';
  bankingUrl = '/banking/unit';

  constructor(
    private _loggerService: LoggerService,
    private _authHttpService: AuthHttpService
  ) {}

  getSettings(): Observable<Settings> {
    this._loggerService.log('getting settings from network');

    return this._authHttpService
      .get(this.settingsUrl, null, this._settingsHeaders)
      .pipe(
        map(({ body }: IResponseInfo) => new Settings(body['settings'])),
        catchError((error) => {
          // TODO: handle errors properly
          console.error(error);

          return observableThrowError(error);
        })
      );
  }

  saveSettings(settings: Settings): Observable<Settings> {
    this._loggerService.log('saving settings:', settings.toJS(), 'to network');

    return this._authHttpService
      .put(this.settingsUrl, serialize(settings).toJS(), this._settingsHeaders)
      .pipe(
        map(({ body }: IResponseInfo) => new Settings(body['updated_settings']))
      );
  }

  resetNumbering(
    documentType: string,
    newNumber: number
  ): Observable<IResponseInfo> {
    const url = `${this.settingsUrl}/doc_numbers/${documentType}`;
    const payload = { number: newNumber };

    return this._authHttpService.put(url, payload);
  }

  saveBankTransfer(
    payload: IBankTransferPayload
  ): Observable<PaymentsSettings> {
    const url = `${this.settingsUrl}/ach_debit`;
    const expectedErrors = [PaymentErrorsEnum.unsupported_country_or_currency];

    this._loggerService.log('saving bank transfer settings to network');

    return this._authHttpService
      .post(
        url,
        payload,
        this._paymentsSettingsHeaders,
        true,
        'json',
        expectedErrors
      )
      .pipe(
        map(({ body }: IResponseInfo) => new PaymentsSettings(body.payments))
      );
  }

  saveCardPaymentsSettings(
    payload: ICardPaymentsPayload
  ): Observable<PaymentsSettings> {
    const url = `${this.settingsUrl}/card_payment`;
    const expectedErrors = [PaymentErrorsEnum.unsupported_country_or_currency];

    this._loggerService.log('saving card payment settings to network');

    return this._authHttpService
      .post(
        url,
        payload,
        this._paymentsSettingsHeaders,
        true,
        'json',
        expectedErrors
      )
      .pipe(
        map(({ body }: IResponseInfo) => new PaymentsSettings(body.payments))
      );
  }

  savePaypalSettings(payload: IPaypalPayload): Observable<PaymentsSettings> {
    const url = `${this.settingsUrl}/paypal_ec`;

    this._loggerService.log('saving paypal settings to network');

    return this._authHttpService
      .post(url, payload, this._paymentsSettingsHeaders)
      .pipe(
        map(({ body }: IResponseInfo) => new PaymentsSettings(body.payments))
      );
  }

  /**
   * It will enable card payments first, before retrieving the URL, so it won’t have the 2005 error.
   * The downside is, it will return an error if enablement fails.
   */
  getStripeOnboardingUrl(returnUrl: string): Observable<string> {
    const url = `${this.settingsUrl}/card_payment/onboarding_url`;
    const params = {
      return_url: returnUrl,
    };

    return this._authHttpService
      .get(url, params, this._paymentsSettingsHeaders)
      .pipe(map(({ body }: IResponseInfo) => body['url']));
  }

  getPayPalOnboardingUrl(returnUrl: string): Observable<string> {
    const url = `${this.settingsUrl}/paypal_ec/onboarding`;
    const params = {};

    if (returnUrl) {
      params['return_url'] = returnUrl;
    }

    return this._authHttpService
      .get(url, params, this._paymentsSettingsHeaders)
      .pipe(map(({ body }: IResponseInfo) => body['onboarding_url']));
  }

  updateCountry(newCountry: string): Observable<Settings> {
    const url = `${this.settingsUrl}/actions/change_country`;
    const payload = { country: newCountry };

    this._loggerService.log('updating country');

    return this._authHttpService
      .post(url, payload, this._settingsHeaders)
      .pipe(
        map(({ body }: IResponseInfo) => new Settings(body['updated_settings']))
      );
  }

  updateCurrency(newCurrency: string): Observable<Settings> {
    const url = `${this.settingsUrl}/actions/change_currency`;
    const payload = { currency_code: newCurrency };

    this._loggerService.log('updating currency');

    return this._authHttpService
      .post(url, payload, this._settingsHeaders)
      .pipe(
        map(({ body }: IResponseInfo) => new Settings(body['updated_settings']))
      );
  }

  updateDocumentLocale(newLocale: string): Observable<Settings> {
    const url = `${this.settingsUrl}/actions/change_document_locale`;
    const payload = { locale: newLocale };

    this._loggerService.log('updating document locale');

    return this._authHttpService
      .post(url, payload, this._settingsHeaders)
      .pipe(
        map(({ body }: IResponseInfo) => new Settings(body['updated_settings']))
      );
  }

  /** I2G MONEY */
  fetchI2gMoneyContext(): Observable<I2gMoneyContext> {
    const url = `${this.bankingUrl}/context`;

    this._loggerService.log('fetching I2g money context');

    return this._authHttpService
      .get(url, {}, this._i2gMoneyHeaders)
      .pipe(map(({ body }: IResponseInfo) => body['context']));
  }

  fetchI2gMoneyWebview(): Observable<I2gMoneyWebview> {
    const url = `${this.bankingUrl}/webview`;

    this._loggerService.log('fetching I2g money webview');

    return this._authHttpService
      .get(url, {}, this._i2gMoneyHeaders)
      .pipe(map(({ body }: IResponseInfo) => body['webview']));
  }

  updateI2gMoneyContext(
    enabled: boolean
  ): Observable<I2gMoneyContext['settings']> {
    const url = `${this.bankingUrl}/settings`;

    this._loggerService.log('updating I2g money context');

    return this._authHttpService
      .put(
        url,
        {
          settings: {
            enabled_by_user: enabled,
          },
        },
        this._i2gMoneyHeaders
      )
      .pipe(map(({ body }: IResponseInfo) => body['settings']));
  }

  createPlaidLinkToken(): Observable<string> {
    const url = `${this.bankingUrl}/create_link_token`;

    this._loggerService.log('creating token for Plaid Link');

    return this._authHttpService
      .post(url, {}, this._i2gMoneyHeaders)
      .pipe(map(({ body }: IResponseInfo) => body['plaid_link_token']));
  }

  linkExternalBankAccount(
    plaidLinkDetails: SubmitPlaidLinkDetailsPayload
  ): Observable<I2gMoneyContext> {
    const url = `${this.bankingUrl}/external_bank_account`;

    this._loggerService.log('linking external bank account');

    return this._authHttpService
      .post(
        url,
        {
          plaid_public_token: plaidLinkDetails.publicToken,
          plaid_selected_account_id: plaidLinkDetails.accountId,
        },
        this._i2gMoneyHeaders
      )
      .pipe(map(({ body }: IResponseInfo) => body['context']));
  }

  unlinkExternalBankAccount(): Observable<I2gMoneyContext> {
    const url = `${this.bankingUrl}/external_bank_account`;

    this._loggerService.log('unlinking external bank account');

    return this._authHttpService
      .delete(url, {}, this._i2gMoneyHeaders)
      .pipe(map(({ body }: IResponseInfo) => body['context']));
  }

  fetchAccountSummary(): Observable<I2gMoneyAccountSummary> {
    const url = `${this.bankingUrl}/transactions`;

    this._loggerService.log('fetching I2g money account summary');

    return this._authHttpService
      .get(url, {}, this._i2gMoneyHeaders)
      .pipe(map(({ body }: IResponseInfo) => body));
  }

  fetchStatements(): Observable<I2gMoneyStatement[]> {
    const url = `${this.bankingUrl}/statements`;

    this._loggerService.log('fetching I2g money statements');

    return this._authHttpService
      .get(url, {}, this._i2gMoneyHeaders)
      .pipe(map(({ body }: IResponseInfo) => body['statements']));
  }

  fetchStatementHtml(statementId: string): Observable<string> {
    const url = `${this.bankingUrl}/statements/${statementId}`;

    this._loggerService.log('fetching I2g money statement html');

    return this._authHttpService
      .get(url, {}, this._i2gMoneyHeaders, 'text')
      .pipe(map(({ body }: IResponseInfo) => body));
  }

  unlinkPayPalAccount(): Observable<any> {
    const url = `${this.settingsUrl}/paypal_ec/unlink`;

    this._loggerService.log('unlinking PayPal account');

    return this._authHttpService
      .post(url, {}, this._paymentsSettingsHeaders)
      .pipe(map(({ body, ..._ }: IResponseInfo) => body));
  }
}
